import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {Component, ElementRef, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {MatSort, Sort} from '@angular/material/sort';
import {PagedRequestDTO} from 'src/app/modules/common/models/PagedRequestDTO';
import {TableControl} from 'src/app/modules/common/models/TableControl';
import {FormControl} from '@angular/forms';
import {MatSnackBar} from '@angular/material/snack-bar';
import {ActivatedRoute, Router} from '@angular/router';
import {MatDialog} from '@angular/material/dialog';
import {ErrorDialogComponent} from '../../../../common/dialogs/error-dialog/error-dialog.component';
import {
  ConfirmationDialogComponent
} from '../../../../common/dialogs/confirmation-dialog/confirmation-dialog.component';
import {Subject, takeUntil} from 'rxjs';
import {debounceTime} from 'rxjs/operators';
import {addFieldMapFiltersForPagedRequestDTO, Common} from '../../../../common/util/Util';
import {HelpDTO} from '../../../../other/models/HelpDTO';

import {DateUtil} from '../../../../common/util/DateUtil';
import {DisplayIcon} from '../../../../common/components';
import {LookupValue} from '../../../../clm/types/lookup-value.enum';
import {PolicyholderGroupService} from '../../../../ent/services/policyholder-group.service';
import {PolicyholderGroupDTO} from '../../../../ent/models/PolicyholderGroupDTO';
import {detailsExpand} from '../../../../common';
import {EntRequestBuilder} from '../../../../ent/services/ent-request-builder';
import {PortalType} from '../../../../common/types/portal.enum';

enum TableColumns {
  Icons = 'icons',
  PolicyholderGroupId = 'policyholderGroupId',
  PolicyholderGroupNumber = 'policyholderGroupNumber',
  Name = 'name',
  Broker = 'broker',
  CentralGroup = 'policyholderGroupParent',
  BusinessType = 'businessType',
  UnderwritingCategory = 'underwritingCategory',
  GroupCountRange = 'groupCountRange',
}

@Component({
  selector: 'greydom-policyholder-groups',
  templateUrl: './policyholder-groups.component.html',
  styleUrls: [],
  animations: [detailsExpand]
})
export class PolicyholderGroupsComponent implements OnInit, OnDestroy {
  readonly debounceTime = Common.DEBOUNCE_TIME;
  readonly destroyed$ = new Subject();
  readonly lookupValue = LookupValue;
  readonly DisplayIcon = DisplayIcon;
  readonly TableColumns = TableColumns;
  readonly PortalType = PortalType;

  policyholderGroupDisplayedColumns: TableColumns[] = Object.values<TableColumns>(TableColumns);
  currentPortal: PortalType = PortalType.Normal;

  // MatSort
  @ViewChild('policyholderGroupSort', {static: false}) policyholderGroupSort: MatSort;

  // MatPaginator Inputs
  @ViewChild('policyholderGroupPaginator', {static: false}) policyholderGroupPaginator: MatPaginator;

  // Form control
  policyholderGroupTextChanged = new Subject<string>();
  policyholderGroupFormControl: FormControl;
  policyholderGroupTableControl: TableControl;
  policyholderGroupExpandedRow: PolicyholderGroupDTO | null;
  policyholderGroupDataSource: PolicyholderGroupDTO[];

  dateFormat = DateUtil.dateFormat;
  dateTimeFormat = DateUtil.dateTimeFormat;
  helpInfo: HelpDTO[];

  length = 100;
  pageSize = 10;
  pageNumber = 0;
  pageSizeOptions: number[] = [5, 10, 25, 100];

  private currentTab: string;
  @ViewChild('policyholderGroupSearch') private policyholderGroupSearch: ElementRef;

  @Input() navigateTo: string;

  constructor(
    public dialog: MatDialog,
    private snackBar: MatSnackBar,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private policyholderGroupService: PolicyholderGroupService,
  ) {
    const parentData = this.activatedRoute.parent?.snapshot.data;
    this.currentPortal = parentData?.portalType || PortalType.Normal;
    this.policyholderGroupFormControl = new FormControl('');

    this.policyholderGroupTextChanged
      .pipe(
        debounceTime(this.debounceTime),
        takeUntil(this.destroyed$)
      ).subscribe(() => {
      this.getListOfPolicyholderGroups(this.policyholderGroupTableControl);
    });
  }

  ngOnInit(): void {
    this.policyholderGroupTableControl = new TableControl(
      this.length,
      this.pageSize,
      this.pageNumber,
      this.pageSizeOptions,
      'policyholderGroupId',
      'desc',
      'name',
      ''
    );

    this.getListOfPolicyholderGroups(this.policyholderGroupTableControl);
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  getInfoTooltip(inputName: string): string {
    let message = '';
    if (this.helpInfo) {
      const helpInfo = this.helpInfo.find((help) => help.inputName === inputName);
      message = helpInfo ? helpInfo.inputInfo : '';
    }
    return message;
  }

  getListOfPolicyholderGroups(tableControl: TableControl): void {
    const pagedRequestDTO = EntRequestBuilder.buildBasicPagedRequestDTO('TblPolicyholderGroup', tableControl);
    const pagedRequestDTOWithFieldMap: PagedRequestDTO = addFieldMapFiltersForPagedRequestDTO(pagedRequestDTO, ['name', 'policyholderGroupNumber'], tableControl.filterValue);

    this.policyholderGroupService.getListOfPolicyholderGroups(pagedRequestDTOWithFieldMap)
      .subscribe({
        next: (response) => {
          this.policyholderGroupDataSource = (response.resultList as PolicyholderGroupDTO[]);
          this.policyholderGroupTableControl.length = response.totalRecords;
        },
        error: (errorResponse) => {
          const error = errorResponse.error;
          this.openErrorDialog(error.businessMessage);
        }
      });
  }

  pageChanged($event: PageEvent) {
    this.policyholderGroupTableControl.pageNumber = $event.pageIndex;
    this.policyholderGroupTableControl.pageSize = $event.pageSize;
    this.getListOfPolicyholderGroups(this.policyholderGroupTableControl);
  }

  sortData($event: Sort) {
    if (this.policyholderGroupPaginator != null) {
      this.policyholderGroupPaginator.firstPage();
    }
    if ($event.active === TableColumns.Broker) {
      this.policyholderGroupTableControl.sortField = 'brokerByBrokerId.name';
    }
    else if ($event.active === TableColumns.BusinessType) {
      this.policyholderGroupTableControl.sortField = 'businessTypeByBusinessTypeId.name';
    }
    else if ($event.active === TableColumns.UnderwritingCategory) {
      this.policyholderGroupTableControl.sortField = 'underwritingCategoryByUnderwritingCategoryId.description';
    }
    else if ($event.active === TableColumns.GroupCountRange) {
      this.policyholderGroupTableControl.sortField = 'groupCountRangeByGroupCountRangeId.groupCountRangeId';
    }
    else if ($event.active === TableColumns.CentralGroup) {
      this.policyholderGroupTableControl.sortField = 'policyholderGroupParentIdByPolicyholderGroupId.name';
    }
    else {
      this.policyholderGroupTableControl.sortField = $event.active;
    }

    this.policyholderGroupTableControl.pageNumber = 0;
    this.policyholderGroupTableControl.sortDirection = $event.direction;
    this.getListOfPolicyholderGroups(this.policyholderGroupTableControl);
  }

  deleteEntry(event: MouseEvent, id: number) {
    event.preventDefault();
    event.stopPropagation();

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '40vw',
      data: 'Are you sure you wish to delete this entity?'
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.policyholderGroupService.deletePolicyholderGroup(id).subscribe({
          next: () => {
            this.openSnackBar(`Policyholder Group ${id} deleted`, 'Dismiss');
            this.getListOfPolicyholderGroups(this.policyholderGroupTableControl);
          },
          error: (errorResponse) => {
            const error = errorResponse.error;
            this.openErrorDialog(error.businessMessage);
          }
        });
      }
    });
  }

  editEntry(event: MouseEvent, id: number) {
    event.preventDefault();
    event.stopPropagation();
    this.router.navigate([this.getRouteForEditPolicyholderGroup(id)]);
  }

  getRouteForAddPolicyholderGroup(): string {
    const routes = {
      [this.PortalType.Normal]: '/enterprise-management/add-policyholder-group',
      [this.PortalType.OnBoarding]: '/onboarding/add-policyholder-group',
      [this.PortalType.Broker]: '/broker/add-policyholder-group'
    };
    return routes[this.currentPortal] || routes[this.PortalType.Normal];
  }

  getRouteForEditPolicyholderGroup(id: number): string {
    const routes = {
      [this.PortalType.Normal]: `/enterprise-management/edit-policyholder-group/${id}`,
      [this.PortalType.OnBoarding]: `/onboarding/edit-policyholder-group/${id}`,
      [this.PortalType.Broker]: `/broker/edit-policyholder-group/${id}`
    };
    return routes[this.currentPortal] || routes[this.PortalType.Normal];
  }

  openSnackBar(message: string, action: string) {
    this.snackBar.open(message, action, {
      duration: 10000
    });
  }

  openErrorDialog(errorMessage: string) {
    const dialogRef = this.dialog.open(ErrorDialogComponent, {
      width: '40vw',
      data: errorMessage
    });
  }

  searchPolicyholderGroup($event) {
    this.policyholderGroupTableControl.filterField = 'name';
    this.policyholderGroupTableControl.filterValue = $event.target.value;
    this.policyholderGroupTextChanged.next($event.target.value);
  }
}
